import { Injectable } from '@angular/core'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { TaxMethodEnum } from 'libs/gb-front/auction/data-access/src'
import { BehaviorSubject, combineLatest, map } from 'rxjs'
import { GbPaymentDialogConfig, GbPaymentDynamicStep, PaymentTypeEnum } from './gb-payment.models'
import { GbRoundHalfEvenService } from './gb-round-half-even/gb-round-half-even.service'
import { ItemEntity } from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.models'
import { EventTicketPackageEntity } from 'libs/gb-front/event-tickets/data-access-event-ticket-packages/src/lib/+state/event-ticket-packages.models'
/**
 * GbPaymentService
 * Service to set, expose and combine all of the streams of charges (payment method fee, tipping fee, base tip amount, tax)
 *
 * The below is the same formula the api uses to calculate the final charge,
 * this has been abstracted into observable streams so that they can be
 * exposed and displayed on the app at different stages.
 *
 * percentage-fee-toFinalCharge = (amount + tax + flat-fee) / (1 - percent)
 * or
 * percentage-fee = (amount + tax) * percent / 100
 *
 * tip = (amount + tax) * tip-percentage
 *
 * Total-charge = roundHalfEven(x + percentage-fee + flat-fee + tip)
 */

@Injectable({
  providedIn: 'root',
})
export class GbPaymentService {
  //4m 40s the api can invalidate the payment intent in 5 min
  paymentSessionTime = 280000
  paymentSessionExpiredSubject$ = new BehaviorSubject<boolean>(false)
  paymentSessionExpired$ = this.paymentSessionExpiredSubject$.asObservable()

  paymentItem$ = new BehaviorSubject<ItemEntity | EventTicketPackageEntity | undefined>(undefined)
  paymentItemQuantity$ = new BehaviorSubject<number>(0)
  paymentDynamicStep$ = new BehaviorSubject<GbPaymentDynamicStep | undefined>(undefined)

  /**
   * Type of payment being made
   * This can be an item, event ticket, checkout or pre auth payment
   */

  paymentTypeSubject$ = new BehaviorSubject<PaymentTypeEnum | undefined>(undefined)
  paymentType$ = this.paymentTypeSubject$.asObservable()

  /**
   * Value of the payment being made
   */

  paymentAmountSubject$ = new BehaviorSubject<number>(0)
  paymentAmount$ = this.paymentAmountSubject$.asObservable()

  /**
   * Taxes
   *
   * tax + payment amount forms the base for the percentage fees to be calculated
   */

  taxes$ = this.auctionFacade.allTaxes$
  taxDisplayAmount$ = combineLatest([this.paymentItem$, this.taxes$, this.paymentAmount$]).pipe(
    map(([item, taxes, paymentAmount]) => {
      let tax
      let taxDisplayAmount = 0

      if (item && 'tax' in item) tax = taxes.find((tax) => tax.uuid === item.tax)
      if (tax && tax.method == TaxMethodEnum.Add) taxDisplayAmount += (paymentAmount * +tax.tax_rate) / 100

      return taxDisplayAmount
    }),
  )
  amountWithTax$ = combineLatest([this.paymentAmount$, this.taxDisplayAmount$]).pipe(
    map(([paymentAmount, taxDisplayAmount]) => paymentAmount + taxDisplayAmount),
  )

  /**
   * Payment method fees
   *
   * percentage-fee = ( paymentAmount + tax + flat-fee) * percent / 100
   * percentage-fee-toFinalCharge = ( paymentAmount + tax + flat-fee) / (1 - percent)
   *
   */

  applyFeeToFinalCharge$ = this.auctionFacade.applyFeeToFinalCharge$
  paymentMethodFlatFee$ = this.auctionFacade.paymentMethodFlatFee$
  formattedFlatFee$ = this.paymentMethodFlatFee$.pipe(map((fee) => (fee ? +fee : 0)))
  paymentMethodPercentageFeePercent$ = this.auctionFacade.paymentMethodPercentageFee$
  formattedPercent$ = this.paymentMethodPercentageFeePercent$.pipe(map((percent) => (percent ? +percent : 0)))

  /**
   * Tipping fees
   */

  tippingFeeAmountSubject$ = new BehaviorSubject<number>(0)
  tippingFeeAmount$ = this.tippingFeeAmountSubject$.asObservable()

  //Donation fields
  donationAmountSubject$ = new BehaviorSubject<number>(0)
  donationAmount$ = this.donationAmountSubject$.asObservable()
  percentFee$ = combineLatest([
    this.paymentAmount$,
    this.taxDisplayAmount$,
    this.formattedFlatFee$,
    this.formattedPercent$,
    this.applyFeeToFinalCharge$,
    this.tippingFeeAmount$,
    this.donationAmount$,
  ]).pipe(
    map(([amount, tax, flatFee, percent, applyFeeToFinalCharge, tippingFeeAmount, donationAmount]) => {
      const subtotal = amount + tippingFeeAmount + donationAmount
      return this.getPercentFee(subtotal, tax, flatFee, percent, applyFeeToFinalCharge)
    }),
  )
  paymentMethodFeeDisplayAmount$ = combineLatest([this.formattedFlatFee$, this.percentFee$]).pipe(
    map(([flatFee, percentFee]) => flatFee + percentFee),
  )
  applyPaymentMethodFee$ = new BehaviorSubject<boolean>(true)

  /**
   * This is the amount to display as the payment total
   *
   * Note we do not send this amount to the api - the api
   * will calculate all of this based on the payload we supply
   *
   */

  totalDisplayAmount$ = combineLatest([
    this.paymentAmount$,
    this.taxDisplayAmount$,
    this.applyPaymentMethodFee$,
    this.percentFee$,
    this.formattedFlatFee$,
    this.tippingFeeAmount$,
    this.donationAmount$,
  ]).pipe(
    map(([amount, tax, applyFee, percentFee, flatFee, tipAmount, donationAmount]) => {
      let total = amount + donationAmount

      total += tax

      if (applyFee) {
        total += percentFee
        total += flatFee
      }

      total += tipAmount
      return this.gbRoundHalfEvenService.roundHalfEven(total)
    }),
  )

  /**
   * Calculates the amount needed to round up a payment to the next whole number;
   * predicting any service fee adjustments required after rounding. The result is
   * the difference between the initial total amount and the rounded total amount.
   * If amount is already a whole number, returns 1 to round up to next whole number.
   */
  roundUpAmount$ = combineLatest([
    this.paymentAmount$,
    this.taxDisplayAmount$,
    this.formattedFlatFee$,
    this.formattedPercent$,
    this.applyFeeToFinalCharge$,
    this.applyPaymentMethodFee$,
  ]).pipe(
    map(([amount, tax, flatFee, percent, applyFeeToFinalCharge, applyFee]) => {
      let totalAmountBeforeRounding = amount + tax

      if (applyFee) {
        // calculate fees based on the initial amount
        const initialPercentFee = this.getPercentFee(amount, tax, flatFee, percent, applyFeeToFinalCharge)
        const totalInitialFees = initialPercentFee + flatFee
        // add fees to the initial amount
        totalAmountBeforeRounding += totalInitialFees
      }

      // calculate the difference needed to round up to the nearest whole number
      const roundingDifference = Math.ceil(totalAmountBeforeRounding) - totalAmountBeforeRounding

      // add any additional fees that will incur due to rounding
      const additionalPercentFeeDueToRounding = applyFee ? (roundingDifference * percent) / 100 : 0
      const totalAmountAfterRounding = totalAmountBeforeRounding + additionalPercentFeeDueToRounding

      // calculate the difference needed to round up to the nearest whole number
      const finalRoundingDifference = Math.ceil(totalAmountAfterRounding) - totalAmountAfterRounding
      // if no difference (amount is whole number) return 1 otherwise return difference
      return finalRoundingDifference === 0 ? 1 : Math.round(finalRoundingDifference * 100) / 100
    }),
  )

  constructor(private auctionFacade: AuctionFacade, private gbRoundHalfEvenService: GbRoundHalfEvenService) {}

  getPercentFee(amount: number, tax: number, flatFee: number, percent: number, applyFeeToFinalCharge: boolean): number {
    if (applyFeeToFinalCharge) {
      return (amount + tax + flatFee) / (1 - percent / 100) - amount - flatFee - tax
    } else if (percent) {
      return ((amount + tax) * percent) / 100
    }
    return 0
  }

  setPaymentSessionExpired(value: boolean): void {
    this.paymentSessionExpiredSubject$.next(value)
  }

  initPayment(config: GbPaymentDialogConfig): void {
    this.paymentTypeSubject$.next(config.paymentType)
    this.paymentAmountSubject$.next(config.paymentAmount || 0)
    this.paymentItem$.next(config.item)
    this.paymentItemQuantity$.next(config.quantity || 0)
    this.paymentDynamicStep$.next(config.dynamicStep)
  }

  clearPayment(): void {
    this.paymentTypeSubject$.next(undefined)
    this.paymentAmountSubject$.next(0)
    this.paymentItem$.next(undefined)
    this.paymentItemQuantity$.next(0)
    this.donationAmountSubject$.next(0)
    this.tippingFeeAmountSubject$.next(0)
  }
}
